import React, {
    useContext,
    useState,
    useRef,
    useEffect,
    useCallback,
} from 'react';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { useIsMobile } from '../../../Common/hooks/useIsMobile';
import { CapabilityContext } from '../../../Common/components/Capabilities/CapabilityContext';
import { Capability } from '../../../Common/components/Capabilities/capability';
import { HubHeader } from '../../../Common/components/HubHeader';
import { sendChat } from '../../../Common/api/ABHub/ChatApi';
import {
    PrimaryButton,
    DefaultButton,
    Stack,
    TextField,
    Spinner,
    SpinnerSize,
    Text,
    IconButton,
} from '@fluentui/react';

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const AssistantChat: React.FC = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();
    const capabilities = useContext(CapabilityContext);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isSending]);

    const sendMessage = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed || isSending) {
            return;
        }

        const userMessage: ChatMessage = {
            id: newId(),
            role: 'user',
            content: trimmed,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setIsSending(true);

        try {
            const response = await sendChat(trimmed);
            const replyText = response.tool
                ? `${response.reply}\n\n_(tool: ${response.tool})_`
                : response.reply;
            const reply: ChatMessage = {
                id: newId(),
                role: 'assistant',
                content: replyText,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, reply]);
        } catch (err) {
            const reply: ChatMessage = {
                id: newId(),
                role: 'assistant',
                content: `Error contacting assistant: ${
                    (err as Error).message
                }`,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, reply]);
        } finally {
            setIsSending(false);
        }
    }, [input, isSending]);

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void sendMessage();
        }
    };

    const clearConversation = () => {
        setMessages([]);
    };

    if (!capabilities.check(Capability.srmAdminRole)) {
        return null;
    }

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    paddingTop: isMobile ? '16px' : '0',
                    maxWidth: '100%',
                }}
            >
                <div
                    style={{
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        boxShadow:
                            '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                        overflow: 'hidden',
                        marginBottom: '16px',
                    }}
                >
                    <HubHeader
                        title="SRM Assistant"
                        subtitle="Chat with the SRM Assistant"
                        actions={
                            <DefaultButton
                                text="Clear"
                                iconProps={{ iconName: 'Delete' }}
                                onClick={clearConversation}
                                disabled={messages.length === 0 || isSending}
                            />
                        }
                    />

                    <div
                        style={{
                            padding: isMobile ? '12px' : '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                    >
                        {/* Conversation transcript */}
                        <div
                            ref={scrollRef}
                            role="log"
                            aria-live="polite"
                            aria-label="Assistant chat conversation"
                            style={{
                                border: `1px solid ${theme.palette.neutralLight}`,
                                borderRadius: '4px',
                                backgroundColor:
                                    theme.palette.neutralLighterAlt,
                                padding: '12px',
                                height: isMobile ? '50vh' : '55vh',
                                overflowY: 'auto',
                            }}
                        >
                            {messages.length === 0 && !isSending && (
                                <Text
                                    variant="medium"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                            fontStyle: 'italic',
                                        },
                                    }}
                                >
                                    No messages yet. Type a prompt below to
                                    start a conversation with the assistant.
                                </Text>
                            )}

                            <Stack tokens={{ childrenGap: 10 }}>
                                {messages.map((m) => {
                                    const isUser = m.role === 'user';
                                    return (
                                        <Stack
                                            key={m.id}
                                            horizontalAlign={
                                                isUser ? 'end' : 'start'
                                            }
                                        >
                                            <div
                                                style={{
                                                    maxWidth: '80%',
                                                    backgroundColor: isUser
                                                        ? theme.palette
                                                              .themeLighter
                                                        : theme.palette.white,
                                                    border: `1px solid ${
                                                        isUser
                                                            ? theme.palette
                                                                  .themeLight
                                                            : theme.palette
                                                                  .neutralLight
                                                    }`,
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                }}
                                            >
                                                <Text
                                                    variant="small"
                                                    styles={{
                                                        root: {
                                                            color: theme.palette
                                                                .neutralSecondary,
                                                            display: 'block',
                                                            marginBottom: '2px',
                                                            fontWeight: 600,
                                                        },
                                                    }}
                                                >
                                                    {isUser
                                                        ? 'You'
                                                        : 'Assistant'}
                                                    {' \u00B7 '}
                                                    {m.timestamp.toLocaleTimeString()}
                                                </Text>
                                                <Text
                                                    variant="medium"
                                                    styles={{
                                                        root: {
                                                            whiteSpace:
                                                                'pre-wrap',
                                                            wordBreak:
                                                                'break-word',
                                                        },
                                                    }}
                                                >
                                                    {m.content}
                                                </Text>
                                            </div>
                                        </Stack>
                                    );
                                })}
                                {isSending && (
                                    <Stack
                                        horizontal
                                        verticalAlign="center"
                                        tokens={{ childrenGap: 8 }}
                                    >
                                        <Spinner size={SpinnerSize.small} />
                                        <Text variant="small">
                                            Assistant is thinking...
                                        </Text>
                                    </Stack>
                                )}
                            </Stack>
                        </div>

                        {/* Composer */}
                        <Stack
                            horizontal
                            tokens={{ childrenGap: 8 }}
                            verticalAlign="end"
                        >
                            <Stack.Item grow>
                                <TextField
                                    label="Message"
                                    placeholder="Ask the assistant something. Press Enter to send, Shift+Enter for newline."
                                    ariaLabel="Message to assistant"
                                    multiline
                                    autoAdjustHeight
                                    rows={2}
                                    resizable={false}
                                    value={input}
                                    onChange={(_, v) => setInput(v ?? '')}
                                    onKeyDown={handleKeyDown}
                                    disabled={isSending}
                                />
                            </Stack.Item>
                            <Stack
                                tokens={{ childrenGap: 4 }}
                                horizontalAlign="end"
                            >
                                <PrimaryButton
                                    text="Send"
                                    iconProps={{ iconName: 'Send' }}
                                    onClick={() => void sendMessage()}
                                    disabled={input.trim() === '' || isSending}
                                />
                                <IconButton
                                    iconProps={{ iconName: 'Cancel' }}
                                    title="Clear input"
                                    ariaLabel="Clear input"
                                    onClick={() => setInput('')}
                                    disabled={input === '' || isSending}
                                />
                            </Stack>
                        </Stack>
                    </div>
                </div>
            </div>
        </div>
    );
};
